/* Who can do what (milestone 6 roles), in one place so pages agree. */
import type { ClientAccess, Me, MeOrg, OrgKind, Role } from "../api/types";

export const ROLE_LABEL: Record<Role, string> = {
  owner: "Owner",
  admin: "Admin",
  developer: "Developer",
  billing: "Billing",
  viewer: "Viewer",
  client: "Client",
};

/** What each role means, in a sentence, for invite forms and the Clients page. */
export const ROLE_WORDS: Record<Role, string> = {
  owner: "Everything, including billing and choosing other owners.",
  admin: "Everything except billing: servers, sites, domains, DNS, mail and people.",
  developer: "Works on sites: deploys, backups, mail and one-click DNS. Can't add servers, delete sites, edit DNS by hand or manage people.",
  billing: "The Billing page: invoices, payment methods and domain charges. Can look at the rest but not change it.",
  viewer: "Can look at everything and change nothing.",
  client: "Sees the sites and mail you share with them, as Clients › What clients can do sets it. Asks for a restore instead of running one.",
};

/**
 * What each role can and can't do, as a few words each: the invite dialog's role cards. Worded for the org: a
 * client org has no servers, so they aren't named there. A client's comes from what clients can do (`client`).
 */
export function roleCan(role: Role, kind: OrgKind, client?: ClientAccess | null): { can: string[]; cant: string[] } {
  const servers = kind !== "client";
  switch (role) {
    case "owner":
      return { can: ["Everything", "Billing", "Choosing owners"], cant: [] };
    case "admin":
      return { can: ["Sites", ...(servers ? ["Servers"] : []), "DNS and mail", "People"], cant: ["Billing"] };
    case "developer":
      return { can: ["Deploys", "Backups", "Mail", "One-click DNS"], cant: [...(servers ? ["Servers"] : []), "Deleting sites", "DNS by hand", "People"] };
    case "billing":
      return { can: ["Invoices", "Payment methods", "Domain charges"], cant: ["Changing sites"] };
    case "viewer":
      return { can: ["Seeing everything"], cant: ["Changing anything"] };
    case "client": {
      const c = client ?? { sites: true, wpSignIn: false, email: true, dns: true, restores: true };
      const on = [c.sites ? "Sites and health" : null, c.sites && c.wpSignIn ? "WordPress admin" : null, c.email ? "Mailboxes" : null, c.dns ? "Seeing DNS" : null, c.restores ? "Asking for restores" : null].filter((x): x is string => !!x);
      return { can: on, cant: ["Deploys", "Restores", "Settings", "Billing"] };
    }
  }
}

/** The same, said to the person being invited. */
export const ROLE_WORDS_YOU: Record<Role, string> = {
  owner: "You'll be able to do everything, including billing.",
  admin: "You'll manage everything except billing: servers, sites, domains, DNS, mail and people.",
  developer: "You'll work on sites: deploys, backups, mail and one-click DNS.",
  billing: "You'll see invoices, payment methods and domain charges.",
  viewer: "You'll be able to look at everything, without changing anything.",
  client: "You'll see the sites and mail your agency shares with you. When you need a backup restored, you ask for it here.",
};

export const isAdmin = (r: Role) => r === "owner" || r === "admin";
/** Billing page: owner and billing. */
export const canBilling = (r: Role) => r === "owner" || r === "billing";
/**
 * Where the Billing page is linked (the sidebar, a Settings tab): owner and billing of a billed org. The platform org
 * isn't billed; agencies and direct clients are, and an agency's client is part of its agency's bill (`account`).
 */
export const seesBilling = (org: MeOrg) => canBilling(org.role) && org.kind !== "platform" && org.account !== false;
/**
 * New site, in every place it's offered (the Sites page, a server's Web role, the Overview, "N" and the command
 * palette): owners, admins and developers, as the API allows. (The API also refuses a developer limited to some sites.)
 */
export const canCreateSites = (org: MeOrg) => isAdmin(org.role) || org.role === "developer";
/** Registering, renewing and transferring domains costs money: owner and admin. */
export const canBuyDomains = (r: Role) => isAdmin(r);
/** Any DNS record by hand. */
export const canEditDns = (r: Role) => isAdmin(r);
/** One-click records and the records the app manages. */
export const canOneClickDns = (r: Role) => isAdmin(r) || r === "developer";
/** Branding and custom addresses: an agency's owners and admins. */
export const canBrand = (org: MeOrg) => org.kind !== "client" && isAdmin(org.role) && org.ownBranding !== false;
/** Client orgs, invites and moving things between them: an agency's admins (and the platform's, for agencies and direct clients). */
export const canManageClients = (org: MeOrg) => (org.kind === "agency" || org.kind === "platform") && isAdmin(org.role);
/** People who can see Domains at all. Clients see their DNS unless their agency turned it off; billing-only people don't need it. */
export const canSeeDomains = (r: Role, org?: Pick<MeOrg, "client">) => r !== "billing" && (r !== "client" || org?.client?.dns !== false);
/** A client person's switch (What clients can do); everyone else, by their role as before. */
export const clientMay = (org: Pick<MeOrg, "role" | "client">, what: keyof ClientAccess) => org.role !== "client" || org.client?.[what] !== false;
/**
 * Moving environments between servers: Hosterr's team, the platform org's owners and admins (the API's
 * moves/access.ts has the same rule, and GET …/move says `canMove` for an environment exactly). Everyone else sees a
 * move read-only, without server names. One place, so an account's "server access" can come in here later.
 */
export const canMoveEnvironments = (me: Pick<Me, "orgs"> | null | undefined) => !!me?.orgs.some((o) => o.kind === "platform" && isAdmin(o.role));
/**
 * Sees and manages servers in this org (docs/plans.md): platform admins (and the platform org's people), and the
 * owners and admins of an account whose plan has server access. One check, as the API's http/server-access.ts. An
 * older API that doesn't say yet: by role, as before.
 */
export const canSeeServers = (org: MeOrg) => org.servers ?? org.role !== "client";
/** May add a server with the installer here: platform admins, and accounts on "own servers". */
export const canConnectServers = (org: MeOrg) => org.connectServers ?? isAdmin(org.role);
